import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import Icon from '../components/Icon';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { relativeTime } from '../lib/format';
import { ICONS } from '../lib/icons';

// One compute instance, read live from the provider (GET
// /providers/{id}/instances/{ocid}). Reached from the Compute service list,
// which carries the OCID this page is keyed by — a live instance is never
// stored, so there is no database id to route on.
//
// Every value here is the provider's. Nothing is stored, nothing is computed,
// and a field the provider did not report reads as "—" rather than as a zero or
// a guess: on a page whose whole job is to describe one machine, an invented
// blank is worse than an admitted one.

// A row is only rendered when there is something to say. Passing a value of 0
// deliberately still renders — 0 GPUs is an answer — so callers pass undefined
// or '' for "not reported".
const Row = ({ label, children }) =>
  children === '' || children == null ? null : (
    <div>
      <span className="kv__k">{label}</span>
      <span>{children}</span>
    </div>
  );

// Numbers from a shape configuration are whole in every ordinary case (2 OCPU,
// 16 GB) but flexible shapes allow fractions, so trailing zeros are dropped
// rather than a fixed precision imposed.
const num = (v) =>
  v == null ? '' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });

export default function InstanceDetail() {
  const { providerId, instanceId } = useParams();
  const { setNav } = useSideNav();

  const { data, loading, error, reload } = useAsync(
    () => api.getComputeInstance(providerId, instanceId),
    [providerId, instanceId]
  );

  // Keep the Compute service menu while this page is mounted, so stepping into
  // an instance does not drop the reader back to the console nav.
  //
  // The list is scoped by region *and* compartment, so going back carries both of
  // this instance's: one reached in ap-singapore-1 must not land back on the
  // default region's list, and one in a child compartment must not land on a list
  // that happens to exclude it. Known only once the instance has loaded.
  const listPath = `/services/${providerId}/compute`;
  const backQuery = new URLSearchParams();
  if (data?.region) backQuery.set('region', data.region);
  if (data?.compartmentId) backQuery.set('compartment', data.compartmentId);
  const backPath = backQuery.toString()
    ? `${listPath}?${backQuery}`
    : listPath;
  useEffect(() => {
    setNav({
      title: 'Compute',
      subtitle: 'Instances',
      backTo: backPath,
      items: [
        { label: 'Instances', to: backPath, icon: ICONS.list },
        { label: 'Create instance', to: `${listPath}?action=create`, icon: ICONS.create },
      ],
    });
  }, [setNav, listPath, backPath]);
  useEffect(() => () => setNav(null), [setNav]);

  if (loading) return <Loading label="Loading instance…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const i = data || {};
  const vnics = i.vnics || [];
  // The primary VNIC is what a reader means by "the IP"; the provider returns it
  // first (see oci.instanceVnics), and an instance with none is a real state
  // rather than an error.
  const primary = vnics.find((v) => v.primary) || vnics[0] || null;
  const tags = Object.entries(i.tags || {});

  // Shape names the hardware, the numbers are what this instance resolved to —
  // for a flexible shape those are per-instance, so the shape alone would not
  // say how big the machine is.
  const size = [
    i.ocpus ? `${num(i.ocpus)} OCPU` : '',
    i.memoryGb ? `${num(i.memoryGb)} GB` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="stack">
      <PageHeader
        title={i.name || 'Instance'}
        subtitle={[i.shape, i.region].filter(Boolean).join(' · ')}
        actions={
          <button className="btn btn--ghost" onClick={reload}>
            <Icon name={ICONS.refresh} /> Refresh
          </button>
        }
      />

      <div className="grid grid--kpi">
        <StatCard
          label="Status"
          value={<StatusBadge status={i.status} />}
          // The shared vocabulary collapses STOPPING and TERMINATED onto
          // "stopped", so the provider's own state earns its place beside it.
          hint={i.state || ''}
        />
        <StatCard label="Size" value={size || '—'} hint={i.shape || ''} />
        <StatCard
          label="Public IP"
          value={primary?.publicIp || '—'}
          hint={primary?.publicIp ? 'primary VNIC' : 'no public address'}
        />
        <StatCard
          label="Private IP"
          value={primary?.privateIp || '—'}
          hint={primary?.hostname || (primary ? 'primary VNIC' : 'no interface read')}
        />
      </div>

      <Card className="card--quiet-title" title="Instance">
        <div className="kv">
          <Row label="OCID">
            <code className="mono">{i.id}</code>
          </Row>
          <Row label="Region">
            <code className="mono">{i.region}</code>
          </Row>
          <Row label="Availability domain">{i.zone}</Row>
          <Row label="Fault domain">{i.faultDomain}</Row>
          <Row label="Shape">{i.shape}</Row>
          <Row label="OCPUs">{num(i.ocpus)}</Row>
          <Row label="Memory">{i.memoryGb ? `${num(i.memoryGb)} GB` : ''}</Row>
          <Row label="GPUs">{i.gpus ? num(i.gpus) : ''}</Row>
          <Row label="Network bandwidth">
            {i.networkGbps ? `${num(i.networkGbps)} Gbps` : ''}
          </Row>
          <Row label="Processor">{i.processor}</Row>
          <Row label="Operating system">{i.operatingSystem}</Row>
          <Row label="Image">{i.imageName}</Row>
          <Row label="Image OCID">
            {i.imageId ? <code className="mono">{i.imageId}</code> : ''}
          </Row>
          <Row label="Boot volume">
            {i.bootVolumeGb ? `${num(i.bootVolumeGb)} GB` : ''}
          </Row>
          <Row label="Launch mode">{i.launchMode}</Row>
          <Row label="Compartment">
            {i.compartmentId ? <code className="mono">{i.compartmentId}</code> : ''}
          </Row>
          <Row label="Created">
            {i.createdAt
              ? `${new Date(i.createdAt).toLocaleString()} · ${relativeTime(i.createdAt)}`
              : ''}
          </Row>
        </div>
        <p className="field__hint">
          Read live from the provider. Spend is not on this page: the Compute API
          reports none, so cost comes from the usage report on Cost &amp; Billing.
        </p>
      </Card>

      <Card
        className="card--quiet-title"
        title={`Network interfaces (${vnics.length})`}
      >
        {vnics.length === 0 ? (
          <EmptyState
            title="No interfaces read"
            // Addresses live on a different API from the instance, so this is
            // "not readable" rather than "not attached" — a running instance
            // always has a primary VNIC.
            detail="The instance's VNICs could not be read. An instance normally has a primary interface, so this usually means the credentials may not read Virtual Network resources."
          />
        ) : (
          <div className="table-wrap">
            <table className="table table--dense">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Private IP</th>
                  <th>Public IP</th>
                  <th>Hostname</th>
                  <th>MAC</th>
                  <th>Subnet</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {vnics.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <div className="cell-primary">
                        {v.name || '—'}
                        {v.primary ? (
                          <span className="badge badge--info" style={{ marginLeft: 8 }}>
                            Primary
                          </span>
                        ) : null}
                      </div>
                      <div className="cell-sub mono">
                        <span className="cell-id" title={v.id}>
                          {v.id}
                        </span>
                      </div>
                    </td>
                    <td className="mono">{v.privateIp || '—'}</td>
                    <td className="mono">{v.publicIp || '—'}</td>
                    <td>{v.hostname || '—'}</td>
                    <td className="mono">{v.macAddress || '—'}</td>
                    <td className="cell-sub mono">
                      <span className="cell-id" title={v.subnetId || ''}>
                        {v.subnetId || '—'}
                      </span>
                    </td>
                    <td>
                      <StatusBadge status={v.state} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {tags.length > 0 ? (
        <Card className="card--quiet-title" title={`Tags (${tags.length})`}>
          <div className="kv">
            {tags.map(([k, v]) => (
              <Row key={k} label={k}>
                {v || '—'}
              </Row>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
